import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BusinessUnit } from './business-unit.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class BusinessUnitsService {
  private apiUrl = `${environment.restApiUrl}masterdata/business-units`;
  private readonly pickerEntityKey = 'business-unit';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getAllBusinessUnits(params?: any): Observable<Page<BusinessUnit>> {
    return this.http.get<Page<BusinessUnit>>(this.apiUrl, { params }).pipe(catchError(this.handleError));
  }

  getBusinessUnitById(id: number): Observable<BusinessUnit> {
    return this.http.get<BusinessUnit>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  searchBusinessUnits(query: string, page = 0, size = 25): Observable<Page<BusinessUnit>> {
    return forkJoin([
      this.searchBusinessUnitsByField('businessUnitName', query, page, size),
      this.searchBusinessUnitsByField('businessUnitCode', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeBusinessUnitPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentBusinessUnits(limit = 10): Observable<BusinessUnit[]> {
    return from(this.recentPickerCache.loadRecent<BusinessUnit>(this.pickerEntityKey, limit));
  }

  getFreshBusinessUnitById(id: number | string): Observable<BusinessUnit> {
    return this.getBusinessUnitById(Number(id));
  }

  rememberBusinessUnit(businessUnit: BusinessUnit): Observable<void> {
    const record: PickerRecentRecord<BusinessUnit> = {
      key: `${this.pickerEntityKey}::${businessUnit.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: businessUnit.id,
      label: `${businessUnit.businessUnitName ?? ''}${businessUnit.businessUnitCode ? ` - ${businessUnit.businessUnitCode}` : ''}`.trim(),
      payload: businessUnit,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createBusinessUnitPickerAdapter(): EntityDropdownPickerAdapter<BusinessUnit> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentBusinessUnits(limit),
      search: (query: string, page: number, size: number) => this.searchBusinessUnits(query, page, size),
      getFreshById: (id: number | string) => this.getFreshBusinessUnitById(id),
      rememberSelection: (businessUnit: BusinessUnit) => this.rememberBusinessUnit(businessUnit),
      getId: (businessUnit: BusinessUnit) => businessUnit.id,
      getLabel: (businessUnit: BusinessUnit) => `${businessUnit.businessUnitName ?? businessUnit.businessUnitCode ?? businessUnit.id ?? ''}${businessUnit.businessUnitCode && businessUnit.businessUnitName ? ` - ${businessUnit.businessUnitCode}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'businessUnitName,businessUnitCode',
    };
  }

  createBusinessUnit(businessUnit: Partial<BusinessUnit>): Observable<BusinessUnit> {
    return this.http.post<BusinessUnit>(this.apiUrl, businessUnit).pipe(catchError(this.handleError));
  }

  updateBusinessUnit(businessUnit: Partial<BusinessUnit>): Observable<BusinessUnit> {
    return this.http
      .put<BusinessUnit>(`${this.apiUrl}/${businessUnit.id}`, businessUnit)
      .pipe(catchError(this.handleError));
  }

  deleteBusinessUnit(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchBusinessUnitsByField(
    field: 'businessUnitName' | 'businessUnitCode',
    query: string,
    page: number,
    size: number
  ): Observable<Page<BusinessUnit>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<BusinessUnit>>(this.apiUrl, { params }).pipe(
      catchError(() => of(this.emptyBusinessUnitPage(page, size)))
    );
  }

  private mergeBusinessUnitPages(
    firstPage: Page<BusinessUnit>,
    secondPage: Page<BusinessUnit>,
    page: number,
    size: number
  ): Page<BusinessUnit> {
    const merged = new Map<number, BusinessUnit>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((businessUnit) => {
      if (businessUnit?.id !== undefined && businessUnit?.id !== null) {
        merged.set(businessUnit.id, businessUnit);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyBusinessUnitPage(page: number, size: number): Page<BusinessUnit> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
